import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  Person as UserIcon,
  SmartToy as BotIcon,
  SmartToy as SmartToyIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { OciAiTestDto } from '../../dto/OciAiTestDto';

interface ChatInterfaceProps {
  messages: OciAiTestDto.ChatMessage[];
  onMessagesChange: (messages: OciAiTestDto.ChatMessage[]) => void;
  onSendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  currentModel: string;
}

/**
 * 채팅 인터페이스 템플릿
 * 
 * @description AI 모델과의 채팅을 위한 UI 컴포넌트
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onMessagesChange,
  onSendMessage,
  isLoading,
  currentModel,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 메시지가 추가될 때마다 스크롤을 아래로
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 메시지 전송
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const messageContent = inputMessage.trim();
    setInputMessage('');

    try {
      await onSendMessage(messageContent);
    } catch (error) {
      console.error('Error sending message:', error);
    }

    // 입력창에 포커스
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // 엔터키 처리
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // 채팅 초기화
  const handleClearMessages = () => {
    onMessagesChange([]);
  };

  // 메시지 복사
  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  // 모델 이름 표시용
  const getModelDisplayName = (modelId: string) => {
    if (modelId.includes('cohere')) return '🔷 Cohere';
    if (modelId.includes('llama')) return '🦙 Llama';
    if (modelId.includes('vision')) return '👁️ Vision';
    return '🤖 AI';
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* 헤더 */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          pb: 1,
          borderBottom: '1px solid',
          borderBottomColor: 'divider'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6">
            채팅 테스트
          </Typography>
          <Chip
            icon={<SmartToyIcon />}
            label={getModelDisplayName(currentModel)}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Stack>
        
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClearMessages}
          size="small"
          disabled={messages.length === 0}
        >
          Clear
        </Button>
      </Box>

      {/* 메시지 영역 */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          mb: 2,
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a1a1a1',
          },
        }}
      >
        {messages.length === 0 ? (
          <Box 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <SmartToyIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              AI와 대화를 시작해보세요!
            </Typography>
            <Typography variant="body2" textAlign="center">
              아래 입력창에 메시지를 입력하고 Enter키를 누르거나<br />
              전송 버튼을 클릭하세요.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {messages.map((message, index) => (
              <Box key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '80%',
                    ml: message.role === 'user' ? 'auto' : 0,
                    mr: message.role === 'assistant' ? 'auto' : 0,
                    backgroundColor: message.role === 'user' 
                      ? 'primary.light' 
                      : 'background.paper',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    position: 'relative',
                    '&:hover .copy-button': {
                      opacity: 1,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    {message.role === 'assistant' && (
                      <BotIcon sx={{ fontSize: 20, mt: 0.2, opacity: 0.7 }} />
                    )}
                    {message.role === 'user' && (
                      <UserIcon sx={{ fontSize: 20, mt: 0.2, opacity: 0.7 }} />
                    )}
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={() => handleCopyMessage(message.content)}
                      className="copy-button"
                      sx={{ 
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        color: 'inherit'
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              </Box>
            ))}
            
            {/* 로딩 인디케이터 */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <BotIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    AI가 응답을 생성하고 있습니다...
                  </Typography>
                </Paper>
              </Box>
            )}
          </Stack>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* 입력 영역 */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="메시지를 입력하세요... (Shift + Enter로 줄바꿈, Enter로 전송)"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            sx={{ minWidth: 100 }}
          >
            전송
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};